import { isPlatformServer } from '@angular/common';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '@mommy/environments/environment';
import { SUPPORTED_QUESTION_TYPES } from '@mommy/models/QuestionInfo.model';
import { Store } from '@ngxs/store';
import { AppSettings } from 'app/app.settings';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  retryWhen,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  Got401,
  Got403UserIsPending,
  Got404UserNotFound,
  RefreshAccessToken,
} from './../state/auth/auth.actions';
import { AuthService } from './auth/auth.service';
import { genericRetryStrategy } from './rxjs-util';
import { StateUtilsService } from './state-utils.service';
import { StorageService } from './storage.service';

// 定義 http 是否要處理 cache
export const HANDLE_CACHE = new HttpContextToken<boolean>(() => false);

// 定義 http 是否不要處理 token
export const IGNORE_TOKEN = new HttpContextToken<boolean>(() => false);

// 定義 http 是否不要處理 執行 Got401 action
export const IGNORE_GOT401ACTION = new HttpContextToken<boolean>(() => false);

// 這些 API 回 401 時「不」視為 token 過期, 不觸發換 token / 登出流程, 錯誤原樣拋回呼叫端：
// - /hospital：既有排除
// - appv1/user/register_device2：推播裝置註冊, 屬非關鍵背景呼叫。後端曾在 token 有效時回 401,
//   若因此換 token（後端會同時作廢舊 token）並登出, 等於讓非關鍵 API 把使用者踢出（2026-09-19 實機重現）
const SKIP_401_HANDLING_URLS = ['/hospital', 'appv1/user/register_device2'];

// 換 token 的 API 本身：它回 401 代表 refresh token 無效, 不可再進入換 token 流程（否則會等待自己而永久卡住）
const REFRESH_TOKEN_URL = 'v1/auth/refresh-token';

// 換 token 失敗時, 用來喚醒等待中請求的訊號（讓它們以錯誤結束, 而不是永久等待）
const REFRESH_FAILED = Symbol('REFRESH_FAILED');

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  private _isServer: boolean;

  constructor(
    public stateUtil: StateUtilsService,
    public store: Store,
    private authService: AuthService,
    private storage: StorageService,
    @Inject(PLATFORM_ID) private platformId
  ) {
    this._isServer = isPlatformServer(this.platformId);
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    console.log('TokenInterceptor: ', request.url);

    let newRequest;

    // appinfo.mommycareyou.com 不加 header, 否則會出現CORS問題
    if (request.url.includes('appinfo.mommycareyou.com')) {
      newRequest = request;
    } else {
      let token;
      if (request.url.includes(environment.VENDER_API_URL)) {
        token = this.stateUtil.getECAccessToken();
      } else {
        token = this.stateUtil.getAuthToken();
      }

      newRequest = this.addHeader(request, token);
      console.log('TokenInterceptor: newRequest', newRequest);
    }

    // // 函式： 更新token後retry api => 換成 private handle401Error 處理
    // const refreshTokenAndRetry = () => {
    //   console.log('refreshTokenAndRetry', request.url);
    //   return this.store.dispatch(new RefreshAccessToken()).pipe(
    //     take(1),
    //     switchMap(() => {
    //       const token = this.stateUtil.getAuthToken();
    //       return next.handle(this.addHeader(request, token));
    //     })
    //   );
    //   // return this.authService.refreshToken().pipe(
    //   //   take(1),
    //   //   switchMap((token) => {
    //   //     console.log('switchMap got token', token);
    //   //     return next.handle(this.addHeader(request, token));
    //   //   })
    //   // );
    // };

    return next.handle(newRequest).pipe(
      concatMap((evt) => {
        // console.log('intercept evt', evt);
        if (evt instanceof HttpResponse) {
          //console.log('http response', evt);

          // 處理 cachedkey
          const cachedkey = evt.headers.get('cachedkey');
          //console.log('cachedkey', cachedkey);

          if (cachedkey) {
            localStorage.setItem(request.url, cachedkey);
          }
          // 處理 cachedkey end

          // 這邊只有 200 的壯態才會執行這邊, graphql 都是 200 (即使裡面有錯誤 也是 200), 所以要在這裡做判斷
          // 一般的 restful api, 要在 下方的 rxjs error handling 那邊做處理
          if (evt.body) {
            try {
              // graphql response errors
              if (evt.body.errors) {
                if (
                  evt.body.errors[0]?.extensions?.response?.statusCode === 401
                ) {
                  console.warn(
                    'graphql got 401 error, try reload token and retry'
                  );
                  // return refreshTokenAndRetry(); // 注意是否會有 無窮迴圈 => 看過log, 不會loop
                  return this.handle401Error(request, next);
                } else if (
                  evt.body.errors[0]?.extensions?.response?.statusCode === 403
                ) {
                  console.warn('graphql got 403 error');
                  if (evt.body.errors[0]?.message === 'USER_IS_PENDING') {
                    console.warn(
                      'UserIsPending, dispatch Got403UserIsPending action.'
                    );
                    this.store.dispatch(new Got403UserIsPending());
                  }
                } else if (
                  evt.body.errors[0]?.extensions?.response?.statusCode === 404
                ) {
                  console.warn('graphql got 404 error');
                  if (evt.body.errors[0]?.message.includes('USER_NOT_FOUND')) {
                    console.warn(
                      'User not found, dispatch Got404UserNotFound action.'
                    );
                    this.store.dispatch(new Got404UserNotFound());
                  }
                }
              }
            } catch (error) {
              console.error(error);
            }
          } else {
            console.log('no body', evt);
          }
        }
        return of(evt);
      }),
      retryWhen(genericRetryStrategy()), // 什麼情境下要重試(目前只focus 500, server端 error時 重試一次)
      catchError((error: HttpErrorResponse) => {
        console.warn('TokenInterceptor: error', error);

        // 401：排除清單內的 API 不視為 token 過期（見 SKIP_401_HANDLING_URLS）
        if (error.status === 401 && !this.shouldSkip401Handling(request)) {
          console.warn('got 401 error', error, error.url);
          return this.handle401Error(request, next, error);
        } else if (error.status === 403) {
          console.warn('got 403 error', error);
          if (error.error?.message === 'USER_IS_PENDING') {
            console.warn('UserIsPending, dispatch Got403UserIsPending action.');
            this.store.dispatch(new Got403UserIsPending());
          }
        } else if (error.status === 404) {
          console.warn('got 404 error', error);
          if (error.error?.message.includes('USER_NOT_FOUND')) {
            console.warn(
              'USER_NOT_FOUND!, dispatch Got404UserNotFound action.'
            );
            this.store.dispatch(new Got404UserNotFound());
          }
        }
        return throwError(error);
      })
    );
  }

  /** 此請求的 401 是否不應觸發換 token / 登出流程 */
  private shouldSkip401Handling(request: HttpRequest<any>): boolean {
    return (
      SKIP_401_HANDLING_URLS.some((u) => request.url.includes(u)) ||
      // 換 token API 自己的 401：直接失敗, 由發起換 token 的那一端處理
      request.url.includes(REFRESH_TOKEN_URL) ||
      // 登出類 API（帶 IGNORE_GOT401ACTION）：token 本來就可能已失效, 不需要為了登出再去換 token,
      // 否則登出後遲到的換 token 結果會把 token 寫回儲存區
      request.context.get(IGNORE_GOT401ACTION)
    );
  }

  /** 依請求目的地取得對應的 token（廠商 API 用 EC token, 其餘用 mommy/eline token） */
  private getTokenFor(request: HttpRequest<any>): string {
    return request.url.includes(environment.VENDER_API_URL)
      ? this.stateUtil.getECAccessToken()
      : this.stateUtil.getAuthToken();
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError?: any
  ) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.store.dispatch(new RefreshAccessToken()).pipe(
        // 注意順序：catchError 必須在 switchMap「之前」, 只攔「換 token 本身失敗」。
        // 舊寫法放在 switchMap 之後, 會把「重送的請求失敗」也當成換 token 失敗而強制登出,
        // 導致任何一支持續回 401 的 API 都能把使用者踢出。
        catchError((err) => {
          this.isRefreshing = false;
          // 喚醒等待中的請求, 讓它們以錯誤結束
          this.refreshTokenSubject.next(REFRESH_FAILED);
          this.store.dispatch(new Got401());
          return throwError(err);
        }),
        switchMap(() => {
          this.isRefreshing = false;
          this.refreshTokenSubject.next(this.stateUtil.getAuthToken());
          // 用新 token 重送；若仍失敗, 錯誤原樣拋回呼叫端, 不登出
          return next.handle(this.addHeader(request, this.getTokenFor(request)));
        })
      );
    }
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) =>
        token === REFRESH_FAILED
          ? throwError(originalError ?? new Error('refresh token failed'))
          : next.handle(this.addHeader(request, this.getTokenFor(request)))
      )
    );
  }

  /** 是否為送往 ELine 後端（environment.API_URL / DEBUG_API_URL）的請求 */
  private isElineApiRequest(url: string): boolean {
    const env: any = environment;
    return [env.API_URL, env.DEBUG_API_URL].some(
      (base) => !!base && url.startsWith(base)
    );
  }

  private addHeader(request: HttpRequest<any>, token: string) {
    if (!token) {
      token = '';
    }

    const header: any = {};

    // 處理 token
    // let needToken = true;
    // const NO_NEED_TOKEN_URL = ['api/login2', 'api/signup2'];

    // NO_NEED_TOKEN_URL.forEach((elem) => {
    //   if (request.url.includes(elem)) {
    //     needToken = false;
    //   }
    // });

    // 處理 header token
    const ignoreToken = request.context.get(IGNORE_TOKEN);
    if (!ignoreToken && token) {
      header.Authorization = `Bearer ${token}`;
    }
    header['api-key'] = 'elineapp';
    // 處理 token end

    // App 能力宣告：只送往 ELine 後端（API_URL），不送到 GraphQL / 廠商 API 等其他主機，
    // 避免其他主機的 CORS 白名單未放行而整個請求被擋
    if (this.isElineApiRequest(request.url)) {
      // 後端依此判斷 App 支援哪些問卷題型；未帶時後端視為只支援 txt,num,opt（舊版 App）
      header['app-question-types'] = SUPPORTED_QUESTION_TYPES.join(',');
      // 僅供後端 log 追查，不參與判斷
      header['app-version'] = AppSettings.APP_VERSION_CODE;
    }

    // 處理 cache
    const handleCache = request.context.get(HANDLE_CACHE);
    console.log('addToken handleCache', handleCache);

    // Server端(SSR) 不處理 cache
    if (!this._isServer) {
      if (handleCache) {
        const cachedkey = localStorage.getItem(request.url);
        header.cachedkey = cachedkey || '';
      }
    }

    // 處理 cache end

    console.log('addToken header', header);

    return request.clone({
      setHeaders: header,
    });
    // if (needToken) {
    //   const header: any = {
    //     'api-key': 'elineapp',
    //     Authorization: 'Bearer ' + token,
    //   };

    //   return request.clone({
    //     setHeaders: header,
    //   });
    // } else {
    //   const header: any = {
    //     'api-key': 'elineapp',
    //   };
    //   return request.clone({
    //     setHeaders: header,
    //   });
    // }
  }
}
